import type { ApiResponse } from "@/types/common";
import { UserStatus } from "@/types/userStatus";
import { privateRequest } from "../axios";
import type { AxiosResponse } from "axios";

export interface UserSocialLink {
  instagramProfileUrl: string | null;
  linkedinProfileUrl: string | null;
  facebookProfileUrl: string | null;
}

export interface UserSettings {
  isPushNotificationEnabled?: boolean;
  language?: string | null;
}

export interface NotificationLog {
  id?: number | string;
  title?: string | null;
  subject?: string | null;
  name?: string | null;
  message?: string | null;
  description?: string | null;
  body?: string | null;
  content?: string | null;
  text?: string | null;
  type?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isRead?: boolean | null;
  read?: boolean | null;
  [key: string]: unknown;
}

export interface UserProfile {
  id: number;
  firstName: string | null;
  lastName: string | null;
  fullName?: string | null;
  email: string;
  alternateEmail?: string | null;
  mobileNo: string | null;
  alternateMobileNo?: string | null;
  location: string | null;
  profileUrl?: string | null;
  bio?: string | null;
  isActive: boolean;
  companyName: string | null;
  companyWebsite: string | null;
  designation: string | null;
  gender: string | null;
  dob: string | null;
  tableNumber?: string | null;
  areaNumber?: string | null;
  status: UserStatus | null;
  statusRemark: string | null;
  userType: string | null;
  socialLink: UserSocialLink | null;
  userSettings?: UserSettings | null;
  businessNeeds?: string[] | null;
  countryId?: number | string | null;
  countryName?: string | null;
  country?: any;
  cityId?: number | string | null;
  cityName?: string | null;
  industries?: Array<{ id: number; categoryName: string } | string> | null;
  servicesOffered?: string[] | null;
  hobbies?: string[] | null;
  frequentTravelCities?: Array<{ id: number; name: string }> | null;
}

export interface UpdateUserPayload {
  firstName?: string | null;
  lastName?: string | null;
  bio?: string | null;
  mobileNo?: string | null;
  location?: string | null;
  companyName?: string | null;
  companyWebsite?: string | null;
  designation?: string | null;
  businessNeeds?: string[] | null;
  gender?: string | null;
  dob?: string | null;
  userType?: string | null;
  socialLink?: UserSocialLink | undefined;
  userSettings?: UserSettings;
  country?: number | string | null;
  countryId?: number | string | null;
  city?: number | string | null;
  cityId?: number | string | null;
  industryCategoryIds?: Array<number | string> | null;
  servicesOffered?: string[] | null;
  hobbies?: string[] | null;
  citiesTravelled?: Array<number | string> | null;
  profileUrl?: string | null;
  tableNumber?: string | null;
  areaNumber?: string | null;
  memberTypeName?: string | null;
}

export interface GetUsersResponse {
  totalRecord: number;
  results: UserProfile[];
}

const ENDPOINTS = {
  PROFILE_UPDATE: "/users",
  PROFILE_ME: "/users/me",
  GET_USERS: "/users",
  GET_USER_BY_ID: (id: number | string) => `/users/${id}`,
  NOTIFICATION_LOG: "/notification-logs",
  NOTIFICATION_LOGS_COUNT: "/notification-logs/count",
  NOTIFICATION_LOGS_MARK_AS_READ: "/notification-logs/mark-as-read",
};

const getUserProfile = async (): Promise<ApiResponse<UserProfile>> => {
  const response: AxiosResponse<ApiResponse<UserProfile>> =
    await privateRequest.get(ENDPOINTS.PROFILE_ME);
  return response.data;
};

const getUsers = async (params?: {
  search?: string;
  pageNo?: number;
  perPage?: number;
  type?: string;
  questionFilters?: string | Record<string, any>;
}): Promise<GetUsersResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append("search", params.search);
  if (params?.pageNo !== undefined) queryParams.append("pageNo", String(params.pageNo));
  if (params?.perPage !== undefined) queryParams.append("perPage", String(params.perPage));
  if (params?.type) queryParams.append("type", params.type);
  
  if (params?.questionFilters) {
    let qfStr = typeof params.questionFilters === "string" 
      ? params.questionFilters 
      : JSON.stringify(params.questionFilters);

    try {
      if (qfStr.includes("%7B") || qfStr.includes("%22")) {
        qfStr = decodeURIComponent(qfStr);
      }
    } catch {
      // keep qfStr as is
    }
    queryParams.append("questionFilters", qfStr);
  }

  const queryString = queryParams.toString();
  const url = `${ENDPOINTS.GET_USERS}${queryString ? `?${queryString}` : ""}`;
  const response: AxiosResponse<ApiResponse<GetUsersResponse>> =
    await privateRequest.get(url);
  return {
    totalRecord: response?.data?.data?.totalRecord ?? 0,
    results: response?.data?.data?.results ?? [],
  };
};

const getUserById = async (
  id: number | string,
): Promise<UserProfile> => {
  const response: AxiosResponse<ApiResponse<UserProfile>> =
    await privateRequest.get(ENDPOINTS.GET_USER_BY_ID(id));
  return response.data.data;
};

const updateUserProfile = async (
  data: UpdateUserPayload,
): Promise<ApiResponse<UserProfile>> => {
  const response: AxiosResponse<ApiResponse<UserProfile>> =
    await privateRequest.put(ENDPOINTS.PROFILE_UPDATE, data);
  return response.data;
};

const getNotificationLogs = async (): Promise<
  ApiResponse<NotificationLog[]>
> => {
  const response: AxiosResponse<ApiResponse<NotificationLog[]>> =
    await privateRequest.get(ENDPOINTS.NOTIFICATION_LOG);
  return response.data;
};

const getNotificationLogsCount = async (): Promise<
  ApiResponse<{ count: number } | number>
> => {
  const response: AxiosResponse<ApiResponse<{ count: number } | number>> =
    await privateRequest.get(ENDPOINTS.NOTIFICATION_LOGS_COUNT);
  return response.data;
};

const getNotificationLogsMarkAsRead = async (): Promise<ApiResponse<null>> => {
  const response: AxiosResponse<ApiResponse<null>> = await privateRequest.put(
    ENDPOINTS.NOTIFICATION_LOGS_MARK_AS_READ,
  );
  return response.data;
};

const uploadProfileImage = async (
  file: File,
): Promise<ApiResponse<{ profileUrl: string }>> => {
  const formData = new FormData();
  formData.append("file", file);
  const response: AxiosResponse<ApiResponse<{ profileUrl: string }>> =
    await privateRequest.put("/users/profile/image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  return response.data;
};

const removeProfileImage = async (): Promise<ApiResponse<{ profileUrl: string | null }>> => {
  const response: AxiosResponse<ApiResponse<{ profileUrl: string | null }>> =
    await privateRequest.put("/users/profile/image");
  return response.data;
};

const UserService = {
  getUserProfile,
  getUserById,
  updateUserProfile,
  getUsers,
  getNotificationLogs,
  getNotificationLogsCount,
  getNotificationLogsMarkAsRead,
  uploadProfileImage,
  removeProfileImage,
};

export {
  getUserProfile,
  getUserById,
  updateUserProfile,
  getUsers,
  getNotificationLogs,
  getNotificationLogsCount,
  getNotificationLogsMarkAsRead,
  uploadProfileImage,
  removeProfileImage,
};
export default UserService;
